import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { Autoclave, AutoclaveType, Frequency, OpenAutoclaveModal, OpenAssignBoxModal, SporetestingResult, OpenSporetestModal, SporetestLog } from "../types";
import { Biobox, TeamMember } from "../../../../types";
import BioAlert from "./BioAlert";
import DownloadSporetestModal from "./DownloadSporetestModal";
import { getHasUnassignedBoxes } from "../utils/helpers";
import UnassignedSporetests from "./UnassignedSporetests";
import AutoclavesInfiniteScroll from "./AutoclavesInfiniteScroll";
import useCustomerNumber from "../../../../hooks/useCustomerNumber";
import { AutoclaveTypeId } from "../enums";
import useCustomer from "../../../../hooks/useCustomer";
import { useAppSelector } from "../../../../redux/store";
import { selectTeamMembers } from "../../../../redux/customer";
import { selectChildTeamMembers } from "../../../../redux/childCustomer";

interface AutoclavesProps {
  autoclaves: Autoclave[];
  autoclaveTypeById: Map<AutoclaveTypeId, AutoclaveType>;
  autoclaveFrequencyById: Map<number, Frequency>;
  bioboxes: Biobox[];
  reload: () => Promise<void>;
  setTabIndex: (tab: number) => void;
  sporetests: SporetestingResult[];
  sporetestLogs: SporetestLog[];
  setAutoclaves: Dispatch<SetStateAction<Autoclave[]>>;
  setSporetests: Dispatch<SetStateAction<SporetestingResult[]>>;
  setOpenAssignBioboxModal: Dispatch<SetStateAction<OpenAssignBoxModal>>;
  setOpenAutoclaveModal: Dispatch<SetStateAction<OpenAutoclaveModal>>;
  setOpenSporetestModal: Dispatch<SetStateAction<OpenSporetestModal>>;
}

const Autoclaves = (props: AutoclavesProps) => {
  const {
    autoclaves,
    autoclaveTypeById,
    autoclaveFrequencyById,
    bioboxes,
    reload,
    setTabIndex,
    sporetests,
    sporetestLogs,
    setAutoclaves,
    setSporetests,
    setOpenAssignBioboxModal,
    setOpenAutoclaveModal,
    setOpenSporetestModal,
  } = props

  const teamMembers = {
    practice: useAppSelector(selectTeamMembers),
    child: useAppSelector(selectChildTeamMembers),
  }

  const customerNumber = useCustomerNumber()

  const customer = useCustomer()

  const [openDownloadModal, setOpenDownloadModal] = useState<boolean>(false);

  const hasUnassignedBoxes = useMemo(() => {
    return getHasUnassignedBoxes(bioboxes)
  }, [bioboxes])

  const unassignedSporetestResults = useMemo(() => {
    return sporetests.filter((test) => {
      return test.autoclave_id === 0 && test.test_result !== null
    })
  }, [sporetests])

  const bioboxesById = useMemo(() => {
    const boxesById = new Map<number, Biobox>(
      bioboxes.map(box => ( [Number(box.box_id), box] ))
    )

    return boxesById
  }, [bioboxes])

  const sporetestsById = useMemo(() => {
    const testsById = new Map<number, SporetestingResult>(
      sporetests.map(test => ( [Number(test.test_result_id), test] ))
    )

    return testsById
  }, [sporetests])

  const sporetestsByAutoclaveId = useMemo(() => {
    const testsByAutoclaveId = new Map<number, SporetestingResult[]>()

    sporetests.forEach(test => {
      testsByAutoclaveId.get(test.autoclave_id)?.push(test)
        ?? testsByAutoclaveId.set(test.autoclave_id, [test]) 
    })

    return testsByAutoclaveId
  }, [sporetests])

  const sporetestLogsByAutoclaveId = useMemo(() => {
    const logsByAutoclaveId = new Map<number, SporetestLog[]>()

    sporetestLogs.forEach(log => {
      const autoclaveId = log.autoclave_id ?? 0

      logsByAutoclaveId.get(autoclaveId)?.push(log)
        ?? logsByAutoclaveId.set(autoclaveId, [log])
    })

    return logsByAutoclaveId
  }, [sporetestLogs])    

  const usersById = useMemo(() => {
    const users = [ ...teamMembers.practice, ...teamMembers.child]

    const testsById = new Map<number, TeamMember>(
      users.map(teamMember => ( [Number(teamMember.login_user_id), teamMember] ))
    )

    return testsById
  }, [teamMembers])

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {hasUnassignedBoxes && <Box sx={{ cursor: "pointer" }} onClick={() => setTabIndex(1)}><BioAlert /></Box>}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button onClick={() => setOpenDownloadModal(true)} variant="contained">Export All</Button>
        <Button sx={{ ml: 1.5 }} variant="contained" onClick={() => setOpenAutoclaveModal({ open: true, autoclave: null })}>Add Autoclave</Button>
      </Box>
      {unassignedSporetestResults.length > 0 && (
        <UnassignedSporetests
          autoclaves={autoclaves}
          bioboxesById={bioboxesById}
          reload={reload}
          unassignedTestResults={unassignedSporetestResults}
          setOpenAssignBioboxModal={setOpenAssignBioboxModal}
          setOpenSporetestModal={setOpenSporetestModal}
        />
      )}
      {
        autoclaves.length === 0 && (
          <Box>There are no autoclaves, please add an autoclave.</Box>
        )
      }
      {
        autoclaves.length > 0 && (
          <AutoclavesInfiniteScroll
            autoclaves={autoclaves}
            customer={customer}
            setAutoclaves={setAutoclaves}
            setSporetests={setSporetests}
            setOpenAssignBioboxModal={setOpenAssignBioboxModal}
            setOpenAutoclaveModal={setOpenAutoclaveModal}
            setOpenSporetestModal={setOpenSporetestModal}
            autoclaveTypeById={autoclaveTypeById}
            autoclaveFrequencyById={autoclaveFrequencyById}
            bioboxesById={bioboxesById}
            sporetestsById={sporetestsById}
            sporetestsByAutoclaveId={sporetestsByAutoclaveId}
            sporetestLogsByAutoclaveId={sporetestLogsByAutoclaveId}
            usersById={usersById}
          />
        )
      }
      {
        openDownloadModal && (
          <DownloadSporetestModal
            autoclaves={autoclaves}
            autoclaveTypeById={autoclaveTypeById}
            autoclaveFrequencyById={autoclaveFrequencyById}
            sporetestsByAutoclaveId={sporetestsByAutoclaveId}
            customer={customer}
            customerNumber={customerNumber}
            open={openDownloadModal}
            handleClose={() => setOpenDownloadModal(false)}
            unassignedTestResults={unassignedSporetestResults}
          />
        )
      }
    </Box>
  )
}

export default Autoclaves;